import { forwardRef } from "react";
import type { Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { ELEMENT_TYPES } from "@/features/analytics";
import Icon from "./icon";

type CommonProps = {
  loading?: boolean;
  className?: string;
  placeholder?: string;
  /**
   * Analytics `section` for this field — set it and the field reports
   * `element_clicked` as a `search_box`.
   *
   * The app has three search entry points (the home shortcut, the overlay, the
   * results bar) and they are indistinguishable in the reports unless each
   * names itself: the overlay opens over the home route, so `page_type` alone
   * cannot tell them apart. Clicks only — a field that is on screen the whole
   * time makes a meaningless impression denominator, hence
   * `data-track-view="off"`.
   */
  trackSection?: string;
};

type InputProps = CommonProps & {
  as?: "input";
  searchQuery?: string;
  setSearchQuery?: Dispatch<SetStateAction<string>>;
  onEnter?: boolean;
  onSubmit?: (value: string) => void;
  onClear?: () => void;
};

type ButtonProps = CommonProps & {
  as: "button";
  onClick?: () => void;
};

type Props = InputProps | ButtonProps;

const FIELD_CLASS =
  "flex items-center w-full rounded-xl bg-background px-4 py-3 h-12 gap-3 relative border border-content4 overflow-hidden";

const SearchField = forwardRef<HTMLInputElement, Props>(function SearchField(
  props,
  ref,
) {
  const { loading, className, placeholder, trackSection } = props;
  const navigate = useNavigate();

  const fieldClass = `${FIELD_CLASS}${className ? ` ${className}` : ""}`;

  // The field declares its own section: `closest()` matches the element itself,
  // so no wrapper is needed and the layout stays untouched.
  const track = trackSection
    ? {
        "data-track-section": trackSection,
        "data-track-type": ELEMENT_TYPES.searchBox,
        "data-track-target": "search",
        "data-track-view": "off",
      }
    : {};

  const placeholderText = (
    <div className="flex items-center gap-2">
      <span>{placeholder ?? "جستجو در"}</span>
      {!placeholder && (
        <span className="text-primary font-bold">آمــــــــــــوزیم</span>
      )}
    </div>
  );

  if (props.as === "button") {
    return (
      <button
        type="button"
        onClick={props.onClick}
        className={`${fieldClass} text-default-400 text-sm w-full`}
        {...track}
      >
        <Icon name="search" size={20} />
        {placeholderText}
      </button>
    );
  }

  const { searchQuery, setSearchQuery, onEnter, onSubmit, onClear } = props;

  return (
    <div className={fieldClass} {...track}>
      <input
        ref={ref}
        type="text"
        value={searchQuery || ""}
        placeholder={placeholder ? "" : undefined}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            if (onSubmit) {
              onSubmit(searchQuery || "");
            } else if (onEnter) {
              navigate(`/search?s=${searchQuery}`);
            }
          }
        }}
        onChange={(e) => {
          if (setSearchQuery) setSearchQuery(e.target.value);
        }}
        className="w-full outline-none px-8"
      />
      <div className="absolute w-full h-full inset-0 flex gap-3 items-center px-4 text-default-400 pointer-events-none text-sm">
        <Icon name="search" size={20} />
        {!searchQuery && placeholderText}
      </div>
      {loading ? (
        <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin absolute left-3" />
      ) : searchQuery ? (
        <button
          type="button"
          onClick={() => {
            if (setSearchQuery) setSearchQuery("");
            if (onClear) onClear();
          }}
          aria-label="پاک کردن"
          className="absolute left-3 text-default-400"
        >
          <Icon name="close" size={18} />
        </button>
      ) : null}
    </div>
  );
});

export default SearchField;
